import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, Video, Phone, MapPin, Clock, Play, Square, Sparkles, Send,
  Paperclip, Trash2, Download, Loader2, X, Mail, FileText, AlertCircle,
} from 'lucide-react';
import { api, fmtDate, relTime } from './api';
import { useDialog } from '../../../hooks/useDialog';

const TYPE_META = {
  discovery: { label: 'Discovery', icon: Sparkles },
  demo:      { label: 'Demo',      icon: Play },
  proposal:  { label: 'Angebot',   icon: FileText },
  closing:   { label: 'Closing',   icon: Send },
  followup:  { label: 'Follow-Up', icon: Mail },
  other:     { label: 'Sonstiges', icon: Video },
};

const LOCATION_META = {
  online:    { label: 'Online',     icon: Video },
  in_person: { label: 'Vor Ort',    icon: MapPin },
  phone:     { label: 'Telefon',    icon: Phone },
};

const STATUS_META = {
  planned:         { label: 'Geplant',          cls: 'bg-slate-100 text-slate-700' },
  in_progress:     { label: 'Live',             cls: 'bg-emerald-100 text-emerald-700 animate-pulse' },
  ended:           { label: 'Beendet',          cls: 'bg-slate-100 text-slate-600' },
  summary_pending: { label: 'Summary bereit',   cls: 'bg-amber-100 text-amber-700' },
  sent:            { label: 'Gesendet',         cls: 'bg-blue-100 text-blue-700' },
  canceled:        { label: 'Abgebrochen',      cls: 'bg-rose-100 text-rose-700' },
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.planned;
  return <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${m.cls}`}>{m.label}</span>;
}

function formatBytes(b) {
  if (!b) return '0 B';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export default function MeetingsTab({ leadId, lead, onChanged }) {
  const dialog = useDialog();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api(`/api/sales/leads/${leadId}/meetings`);
      setMeetings(data.meetings || []);
    } catch (e) {
      console.warn('load meetings failed', e);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const onCreated = (m) => {
    setShowNew(false);
    setSelectedId(m.id);
    load();
  };

  const onMeetingChanged = () => {
    load();
    onChanged?.();
  };

  return (
    <div className="space-y-3">
      {dialog.element}
      <div className="flex items-center justify-between">
        <div className="text-[12px] text-[var(--text-tertiary)]">
          {loading ? 'Lädt…' : `${meetings.length} Meeting${meetings.length === 1 ? '' : 's'}`}
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#338edc] text-white text-[12px] font-medium hover:opacity-90"
        >
          <Plus className="w-3.5 h-3.5" />
          Neues Meeting
        </button>
      </div>

      {meetings.length === 0 && !loading && (
        <div className="flex flex-col items-center justify-center py-10 text-[var(--text-tertiary)]">
          <Video className="w-8 h-8 mb-2 opacity-50" />
          <span className="text-[13px]">Noch keine Meetings</span>
          <button
            onClick={() => setShowNew(true)}
            className="mt-3 text-[12px] text-[#338edc] hover:underline"
          >
            Erstes Meeting anlegen
          </button>
        </div>
      )}

      <div className="space-y-2">
        {meetings.map((m) => {
          const TypeIcon = (TYPE_META[m.meeting_type] || TYPE_META.other).icon;
          const isExpanded = selectedId === m.id;
          return (
            <div
              key={m.id}
              className={`border rounded-lg bg-[var(--bg-card)] transition-colors ${
                isExpanded ? 'border-[#338edc]' : 'border-[var(--border-card)]'
              }`}
            >
              <button
                onClick={() => setSelectedId(isExpanded ? null : m.id)}
                className="w-full px-3 py-2.5 flex items-center gap-2 text-left hover:bg-[var(--bg-inset)] rounded-lg"
              >
                <TypeIcon className="w-4 h-4 text-[var(--text-tertiary)] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-[var(--text-primary)] truncate">{m.title}</div>
                  <div className="flex items-center gap-2 text-[11px] text-[var(--text-tertiary)] mt-0.5">
                    <span>{(TYPE_META[m.meeting_type] || TYPE_META.other).label}</span>
                    <span>•</span>
                    <span>{m.host_user_name || 'Kein Host'}</span>
                    {m.duration_minutes ? <><span>•</span><span>{m.duration_minutes} Min</span></> : null}
                    {m.attachment_count > 0 && (
                      <>
                        <span>•</span>
                        <span className="inline-flex items-center gap-0.5">
                          <Paperclip className="w-2.5 h-2.5" />{m.attachment_count}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <StatusPill status={m.status} />
                <div className="text-[11px] text-[var(--text-tertiary)] flex-shrink-0">
                  {m.sent_at ? relTime(m.sent_at) : (m.scheduled_at ? fmtDate(m.scheduled_at) : relTime(m.created_at))}
                </div>
              </button>

              {isExpanded && (
                <MeetingDetail
                  meetingId={m.id}
                  lead={lead}
                  dialog={dialog}
                  onClose={() => setSelectedId(null)}
                  onChanged={onMeetingChanged}
                />
              )}
            </div>
          );
        })}
      </div>

      {showNew && (
        <NewMeetingModal
          leadId={leadId}
          onClose={() => setShowNew(false)}
          onCreated={onCreated}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  NewMeetingModal — kleines Form für Meeting-Anlage
// ─────────────────────────────────────────────────────────────────────────

function NewMeetingModal({ leadId, onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [meetingType, setMeetingType] = useState('discovery');
  const [location, setLocation] = useState('online');
  const [scheduledAt, setScheduledAt] = useState('');
  const [meetingUrl, setMeetingUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!title.trim()) {
      setError('Titel erforderlich');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const data = await api(`/api/sales/leads/${leadId}/meetings`, {
        method: 'POST',
        body: {
          title: title.trim(),
          meeting_type: meetingType,
          location,
          scheduled_at: scheduledAt || null,
          meeting_url: meetingUrl.trim() || null,
        },
      });
      onCreated(data.meeting);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" onClick={onClose}>
      <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-5 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[14px] font-semibold text-[var(--text-primary)]">Neues Meeting</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--bg-inset)]"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] font-medium text-[var(--text-tertiary)] mb-1">Titel *</label>
            <input
              type="text" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="z.B. Discovery Call mit Sima"
              className="w-full px-3 py-2 rounded-lg border border-[var(--border-card)] bg-[var(--bg-card)] text-[13px] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[11px] font-medium text-[var(--text-tertiary)] mb-1">Typ</label>
              <select value={meetingType} onChange={(e) => setMeetingType(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-[var(--border-card)] bg-[var(--bg-card)] text-[13px]">
                {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-[var(--text-tertiary)] mb-1">Ort</label>
              <select value={location} onChange={(e) => setLocation(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-[var(--border-card)] bg-[var(--bg-card)] text-[13px]">
                {Object.entries(LOCATION_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-[var(--text-tertiary)] mb-1">Termin (optional)</label>
            <input
              type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-[var(--border-card)] bg-[var(--bg-card)] text-[13px]"
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-[var(--text-tertiary)] mb-1">Meeting-URL (optional)</label>
            <input
              type="url" value={meetingUrl} onChange={(e) => setMeetingUrl(e.target.value)}
              placeholder="https://meet.google.com/…"
              className="w-full px-3 py-2 rounded-lg border border-[var(--border-card)] bg-[var(--bg-card)] text-[13px]"
            />
          </div>
          {error && (
            <div className="flex items-center gap-2 text-[12px] text-red-600 bg-red-50 px-2 py-1.5 rounded">
              <AlertCircle className="w-3.5 h-3.5" />{error}
            </div>
          )}
        </div>
        <div className="flex gap-2 justify-end mt-4 pt-3 border-t border-[var(--border-card)]">
          <button onClick={onClose} className="px-3 py-2 text-[12px] text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] rounded-lg">Abbrechen</button>
          <button onClick={submit} disabled={saving || !title.trim()} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#338edc] text-white text-[12px] font-medium hover:opacity-90 disabled:opacity-50">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Anlegen
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  MeetingDetail — Live-Notes, Attachments, AI-Summary, Send
// ─────────────────────────────────────────────────────────────────────────

function MeetingDetail({ meetingId, lead, dialog, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [busy, setBusy] = useState(null); // 'start'|'stop'|'summary'|'send'|null
  const [uploading, setUploading] = useState(false);
  const [editingSummary, setEditingSummary] = useState(false);
  const [draftSubject, setDraftSubject] = useState('');
  const [draftHtml, setDraftHtml] = useState('');
  const fileInputRef = useRef(null);
  const notesTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api(`/api/sales/meetings/${meetingId}`);
      setData(res);
      setNotes(res.meeting.notes_md || '');
      setDraftSubject(res.meeting.ai_summary_subject || '');
      setDraftHtml(res.meeting.ai_summary_md || '');
    } catch (e) {
      console.warn(e);
    } finally {
      setLoading(false);
    }
  }, [meetingId]);

  useEffect(() => { load(); }, [load]);

  // Debounced auto-save für Notes (1s nach letzter Eingabe)
  useEffect(() => {
    if (!data) return;
    if (notes === (data.meeting.notes_md || '')) return;
    if (notesTimer.current) clearTimeout(notesTimer.current);
    notesTimer.current = setTimeout(async () => {
      setSavingNotes(true);
      try {
        await api(`/api/sales/meetings/${meetingId}`, { method: 'PATCH', body: { notes_md: notes } });
      } catch (e) {
        console.warn('auto-save failed', e);
      } finally {
        setSavingNotes(false);
      }
    }, 1000);
    return () => { if (notesTimer.current) clearTimeout(notesTimer.current); };
  }, [notes, data, meetingId]);

  const flushNotes = async () => {
    if (notesTimer.current) clearTimeout(notesTimer.current);
    if (!data || notes === (data.meeting.notes_md || '')) return;
    setSavingNotes(true);
    try {
      await api(`/api/sales/meetings/${meetingId}`, { method: 'PATCH', body: { notes_md: notes } });
    } finally {
      setSavingNotes(false);
    }
  };

  const startMeeting = async () => {
    setBusy('start');
    try {
      await api(`/api/sales/meetings/${meetingId}`, { method: 'PATCH', body: { status: 'in_progress' } });
      await load();
      onChanged?.();
    } finally { setBusy(null); }
  };

  const endMeeting = async () => {
    await flushNotes();
    setBusy('stop');
    try {
      await api(`/api/sales/meetings/${meetingId}`, { method: 'PATCH', body: { status: 'ended' } });
      await load();
      onChanged?.();
    } finally { setBusy(null); }
  };

  const generateSummary = async () => {
    await flushNotes();
    if (!notes.trim()) {
      await dialog.alert('Notizen fehlen', 'Bitte zuerst Meeting-Notizen erfassen, dann AI-Summary generieren.');
      return;
    }
    setBusy('summary');
    try {
      const res = await api(`/api/sales/meetings/${meetingId}/generate-summary`, { method: 'POST' });
      setDraftSubject(res.subject || '');
      setDraftHtml(res.html_body || '');
      setEditingSummary(true);
      await load();
      onChanged?.();
    } catch (e) {
      await dialog.alert('AI-Summary fehlgeschlagen', e.message);
    } finally { setBusy(null); }
  };

  const saveSummaryEdits = async () => {
    setBusy('save_summary');
    try {
      await api(`/api/sales/meetings/${meetingId}`, {
        method: 'PATCH',
        body: { ai_summary_subject: draftSubject, ai_summary_md: draftHtml },
      });
      setEditingSummary(false);
      await load();
    } finally { setBusy(null); }
  };

  const sendMail = async () => {
    if (!data) return;
    if (!data.meeting.lead_email) {
      await dialog.alert('Keine Mail-Adresse', 'Dieser Lead hat keine E-Mail-Adresse hinterlegt. Bitte zuerst Stammdaten ergänzen.');
      return;
    }
    const subject = editingSummary ? draftSubject : data.meeting.ai_summary_subject;
    const html = editingSummary ? draftHtml : data.meeting.ai_summary_md;
    if (!html) {
      await dialog.alert('Keine Zusammenfassung', 'Bitte zuerst AI-Summary generieren oder Inhalt eingeben.');
      return;
    }
    const ok = await dialog.confirm(
      'Follow-Up senden?',
      `Mail an ${data.meeting.lead_email}\nBetreff: ${subject}\nAnhänge: ${(data.attachments || []).length}`,
      { confirmLabel: 'Senden', cancelLabel: 'Abbrechen' }
    );
    if (!ok) return;
    setBusy('send');
    try {
      const res = await api(`/api/sales/meetings/${meetingId}/send`, {
        method: 'POST',
        body: { subject, html_body: html },
      });
      setEditingSummary(false);
      await load();
      onChanged?.();
      await dialog.alert('Gesendet', `Mail an ${res.to} versendet (${res.attachment_count} Anhänge).`);
    } catch (e) {
      await dialog.alert('Send fehlgeschlagen', e.message);
    } finally { setBusy(null); }
  };

  const uploadFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append('file', f, f.name);
      const r = await fetch(`/api/sales/meetings/${meetingId}/attachments`, {
        method: 'POST', body: fd, credentials: 'include',
      });
      const json = await r.json();
      if (!r.ok) throw new Error(json.error || 'Upload fehlgeschlagen');
      await load();
    } catch (e) {
      await dialog.alert('Upload fehlgeschlagen', e.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deleteAttachment = async (att) => {
    const ok = await dialog.confirm('Anhang löschen?', att.original_name, { variant: 'danger', confirmLabel: 'Löschen' });
    if (!ok) return;
    try {
      await api(`/api/sales/meeting-attachments/${att.id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      await dialog.alert('Fehler', e.message);
    }
  };

  if (loading || !data) {
    return <div className="px-3 py-4 text-[12px] text-[var(--text-tertiary)]"><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-2" />Lädt…</div>;
  }

  const m = data.meeting;
  const isPlanned = m.status === 'planned';
  const isLive = m.status === 'in_progress';
  const isEnded = ['ended', 'summary_pending', 'sent'].includes(m.status);
  const isSent = m.status === 'sent';
  const hasSummary = !!(m.ai_summary_md || m.ai_summary_subject);

  return (
    <div className="border-t border-[var(--border-card)] p-4 space-y-4 bg-[var(--bg-inset)]">
      {/* Action-Row: Start / Stop / Generate / Send */}
      <div className="flex flex-wrap items-center gap-2">
        {isPlanned && (
          <button onClick={startMeeting} disabled={busy === 'start'}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-[12px] font-medium hover:opacity-90 disabled:opacity-50">
            {busy === 'start' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
            Meeting starten
          </button>
        )}
        {isLive && (
          <button onClick={endMeeting} disabled={busy === 'stop'}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-700 text-white text-[12px] font-medium hover:opacity-90 disabled:opacity-50">
            {busy === 'stop' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Square className="w-3.5 h-3.5" />}
            Meeting beenden
          </button>
        )}
        {(isLive || isEnded) && !isSent && (
          <button onClick={generateSummary} disabled={busy === 'summary' || !notes.trim()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#338edc] text-white text-[12px] font-medium hover:opacity-90 disabled:opacity-50">
            {busy === 'summary' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {hasSummary ? 'Summary neu generieren' : 'AI-Summary generieren'}
          </button>
        )}
        {hasSummary && !isSent && (
          <button onClick={sendMail} disabled={busy === 'send'}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-[12px] font-medium hover:opacity-90 disabled:opacity-50">
            {busy === 'send' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Follow-Up senden
          </button>
        )}
        {isSent && (
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 text-[12px]">
            <Mail className="w-3.5 h-3.5" />
            Gesendet an {m.sent_to_email} · {fmtDate(m.sent_at)}
          </div>
        )}
      </div>

      {/* Live-Notes Editor */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
            Live-Notizen (Markdown)
          </label>
          <span className="text-[10px] text-[var(--text-tertiary)]">
            {savingNotes ? <><Loader2 className="w-2.5 h-2.5 animate-spin inline mr-1" />Speichern…</> : 'Auto-Speichern aktiv'}
          </span>
        </div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={flushNotes}
          rows={8}
          placeholder={`Stichworte während des Meetings…\n\n• Pain Points\n• Bedarf\n• Nächste Schritte\n• Vereinbarungen`}
          disabled={isSent}
          className="w-full px-3 py-2 rounded-lg border border-[var(--border-card)] bg-[var(--bg-card)] text-[13px] text-[var(--text-primary)] font-mono leading-relaxed focus:outline-none focus:border-[#338edc] disabled:opacity-60"
        />
      </div>

      {/* Attachments */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
            Anhänge ({(data.attachments || []).length})
          </label>
          {!isSent && (
            <>
              <input
                type="file" ref={fileInputRef} multiple
                onChange={(e) => uploadFiles(e.target.files)}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-1 text-[11px] text-[#338edc] hover:underline disabled:opacity-50"
              >
                {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />}
                Datei hinzufügen
              </button>
            </>
          )}
        </div>
        {(data.attachments || []).length === 0 ? (
          <div className="text-[12px] text-[var(--text-tertiary)] italic py-2">Keine Anhänge</div>
        ) : (
          <div className="space-y-1">
            {data.attachments.map((a) => (
              <div key={a.id} className="flex items-center gap-2 px-2 py-1.5 rounded border border-[var(--border-card)] bg-[var(--bg-card)] text-[12px]">
                <Paperclip className="w-3 h-3 text-[var(--text-tertiary)] flex-shrink-0" />
                <span className="flex-1 truncate text-[var(--text-primary)]">{a.original_name}</span>
                <span className="text-[10px] text-[var(--text-tertiary)]">{formatBytes(a.size_bytes)}</span>
                <a href={`/api/sales/meeting-attachments/${a.id}/download`}
                  className="p-1 rounded hover:bg-[var(--bg-inset)] text-[var(--text-tertiary)] hover:text-[#338edc]"
                  title="Download">
                  <Download className="w-3 h-3" />
                </a>
                {!isSent && (
                  <button onClick={() => deleteAttachment(a)}
                    className="p-1 rounded hover:bg-red-50 text-[var(--text-tertiary)] hover:text-red-600"
                    title="Löschen">
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI-Summary Preview / Editor */}
      {hasSummary && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] inline-flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-[#338edc]" />
              AI-Summary (Mail-Entwurf)
              {m.ai_summary_edited && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[9px]">manuell editiert</span>}
            </label>
            {!isSent && (
              <button onClick={() => setEditingSummary(!editingSummary)}
                className="text-[11px] text-[#338edc] hover:underline">
                {editingSummary ? 'Vorschau' : 'Bearbeiten'}
              </button>
            )}
          </div>
          {editingSummary ? (
            <div className="space-y-2">
              <input
                type="text" value={draftSubject} onChange={(e) => setDraftSubject(e.target.value)}
                placeholder="Betreff"
                className="w-full px-3 py-2 rounded-lg border border-[var(--border-card)] bg-[var(--bg-card)] text-[13px] font-medium"
              />
              <textarea
                value={draftHtml} onChange={(e) => setDraftHtml(e.target.value)}
                rows={10}
                placeholder="HTML-Body…"
                className="w-full px-3 py-2 rounded-lg border border-[var(--border-card)] bg-[var(--bg-card)] text-[12px] font-mono"
              />
              <button onClick={saveSummaryEdits} disabled={busy === 'save_summary'}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-[#338edc] text-white text-[11px] font-medium hover:opacity-90 disabled:opacity-50">
                {busy === 'save_summary' ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                Änderungen übernehmen
              </button>
            </div>
          ) : (
            <div className="border border-[var(--border-card)] rounded-lg bg-[var(--bg-card)] overflow-hidden">
              <div className="px-3 py-2 border-b border-[var(--border-card)] bg-[var(--bg-inset)] text-[12px]">
                <div className="text-[10px] text-[var(--text-tertiary)] mb-0.5">Betreff</div>
                <div className="font-medium text-[var(--text-primary)]">{m.ai_summary_subject || draftSubject || '—'}</div>
              </div>
              <div className="px-3 py-2 text-[13px] text-[var(--text-primary)] leading-relaxed prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: m.ai_summary_md || draftHtml || '' }} />
            </div>
          )}
          {m.ai_generated_at && (
            <div className="text-[10px] text-[var(--text-tertiary)] mt-1">
              Generiert {relTime(m.ai_generated_at)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
